import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import SignInModal from './SignInModal';
import './MainPage.css';
import { motion } from "framer-motion";
import MagicRings from './MagicRings';
import { HiArrowTrendingUp, HiLockClosed, HiDevicePhoneMobile, HiBolt, HiArrowRight, HiCheckCircle } from 'react-icons/hi2';
import { FaWhatsapp, FaTelegramPlane, FaInstagram, FaFacebookF, FaYoutube } from 'react-icons/fa';
import ScrollStack, { ScrollStackItem } from './ScrollStack';
import HorizontalScrollCards from './HorizontalScrollCards';
import WhyChooseUs from './WhyChooseUs';
import GlassCandles from './GlassCandles';
import SplashCursor from './SplashCursor';

const MainPage = ({ onSuccessfulLogin }) => {
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSmallViewport, setIsSmallViewport] = useState(false);
  const [expandedPlans, setExpandedPlans] = useState({});
  // Theme switching removed; defaults are provided via CSS variables in index.css

  // Ensure viewport meta tag exists for proper mobile scaling
  useEffect(() => {
    const existingMeta = document.querySelector('meta[name="viewport"]');
    if (existingMeta) {
      existingMeta.setAttribute('content', 'width=device-width, initial-scale=1.0');
    } else {
      const meta = document.createElement('meta');
      meta.name = "viewport";
      meta.content = 'width=device-width, initial-scale=1.0';
      document.head.appendChild(meta);
    }
  }, []);

  useEffect(() => {
    const update = () => {
      setIsSmallViewport(window.innerWidth <= 360);
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const openSignInModal = () => {
    setAuthMode('login');
    setIsSignInModalOpen(true);
  };

  const openSignUpModal = () => {
    setAuthMode('signup');
    setIsSignInModalOpen(true);
  };

  const closeSignInModal = () => {
    setIsSignInModalOpen(false);
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    console.log('Email submitted:', email);
    setEmail('');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="main-page">
      <SplashCursor RAINBOW_MODE={false} COLOR="#A855F7" />
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="logo">
                <img src="/images/Final Font Stocks4U1.png" alt="Stocks4U Logo" className="logo-text" style={{ width: '150px', height: 'auto' }} />
          </div>
          
          <nav className={`nav ${isMobileMenuOpen ? 'nav-open' : ''}`}>
            <a href="#home" onClick={() => setIsMobileMenuOpen(false)}>Home</a>
            <a href="#features" onClick={() => setIsMobileMenuOpen(false)}>Features</a>
            <a href="#pricing" onClick={() => setIsMobileMenuOpen(false)}>Pricing</a>
            <a href="/about" onClick={() => setIsMobileMenuOpen(false)}>About</a>
            <a href="#contact" onClick={() => setIsMobileMenuOpen(false)}>Contact</a>
          </nav>

          <button
            className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          
          <div className="header-buttons">
            <button className="login-btn" onClick={openSignInModal}>Login</button>
            <button className="get-started-btn" onClick={openSignInModal}>Get Started</button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero" id="home" style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
          <MagicRings
            color="#fc42ff"
            colorTwo="#42fcff"
            speed={1}
            ringCount={6}
            attenuation={10}
            lineThickness={2}
            baseRadius={0.35}
            radiusStep={0.1}
            scaleRate={0.1}
            opacity={1}
            blur={0}
            noiseAmount={0.1}
            rotation={0}
            ringGap={1.5}
            fadeIn={0.7}
            fadeOut={0.5}
            followMouse={true}
            mouseInfluence={0.2}
            hoverScale={1.2}
            parallax={0.05}
            clickBurst={true}
          />
        </div>
        <div className="hero-content" style={{ position: 'relative', zIndex: 1, pointerEvents: 'none' }}>
          <h1 className="hero-title" style={{ pointerEvents: 'auto' }}>Watch. Analyse. Trade.</h1>
          <p className="hero-subtitle" style={{ pointerEvents: 'auto' }}>Trade Smarter with AI-driven insights and real time market intelligence.</p>
        </div>
      </section>

      {/* Introducing Section */}
      <section className="introducing-premium-wrap" id="introducing">
        <div className="container">
          <div className="introducing-content-premium">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="introducing-header-premium"
            >
              <h2 className="section-title-premium-new">
                Introducing the Future of <span className="gradient-text-hero">Intelligent Trading</span>
              </h2>
              <h3 className="section-subtitle-premium-new">
                <span className="gradient-text-hero-alt">AI-Powered</span> Precision. Real-Time Execution. Smarter Decisions.
              </h3>
              <p className="introducing-description-premium-new">
                Trading is no longer driven by guesswork - it's powered by data, speed, and intelligent insights.
                Stocks4u combines advanced AI analytics with proven trading strategies to help you make faster, smarter, and more confident decisions in dynamic markets.
              </p>
            </motion.div>
            
            <GlassCandles />

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="introducing-cta-new"
              style={{ textAlign: 'center', marginTop: '2rem' }}
            >
              <p style={{ color: '#9ca3af', marginBottom: '1rem' }}>Start trading smarter today - powered by AI.</p>
              <button className="premium-cta-button-new" onClick={openSignUpModal}>
                Get Started <HiArrowRight className="btn-icon" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Horizontal Scroll Cards Section */}
      <HorizontalScrollCards />

      {/* Why Choose Us Section */}
      <WhyChooseUs />

      {/* Sector Rotation Section */}

      <section className="sector-rotation">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, x: -140 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.35 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="phone1 phone-sector"
          >
            <div className="phone-screen">
              <img src="images/sector rotation.png" className="phone-image" alt="Sector Rotation" />
            </div>
          </motion.div>
          <h2 className="hero-title">Sector Rotation</h2>
           <ul className="ai-points">
            <li>Markets don't move stock by stock - they move Sector by Sector.</li>
            <li>Our AI-powered Sector Rotation engine tracks money flow across sectors in real time, </li>
             <li> revealing which industries are gaining strength and which are losing momentum.</li>
            <li>Trade where the strength is. Exit where the weakness begins.</li>
          </ul> 
        </div>
      </section>

      {/* Real Time Sector Data */}

      {/* Index Mover Section */}
      <section className="index-mover">
        <div className="container">
          <h2 className="hero-title">Index Mover</h2>
          <ul class="ai-points">
            <li>AI-powered insights that reveal the true stocks moving the Index</li>
            <li>Our AI decodes market momentum at its source.</li>
            <li>So you trade with precision, not prediction</li>
          </ul> 

          
          <div className="mover-tabs">
            <div className="tab active">Positive</div>
            <div className="tab">Net Positions</div>
            <div className="tab">Negative</div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, x: 140 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.35 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="phone1 phone-sector"
          >
            <div className="phone-screen">
              <img src="images/index mover.png" className="phone-image" />
            </div>
          </motion.div>
        </div>
      </section>
      

          {/* REPLACED CARD GRID SECTION */}
          

            
          


      {/* Subscriptions Section */}
      <section className="premium-subscriptions" id="pricing">
        <div className="container">
          <div className="subscriptions-header">
             <h2 className="section-title-premium-new">
               Elevate your <span className="gradient-text-hero">Trading Edge</span>
             </h2>
             <p className="subscriptions-subtitle">
                Choose the perfect plan for your journey and get access to institutional-grade AI features.
             </p>
          </div>
          
          <div className="premium-subscription-grid">
            {[
              {
                tier: 'Silver',
                tierClass: 'standard-tier',
                price: '4,999',
                period: '/ 3 months',
                features: [
                  "Intraday Scan", "Sector Rotation", "OI Monitor", "Momentum Radar", 
                  "Heat map", "Swing Zone", "Index Mover", "FII - DII Data", 
                  "Traders Diary", "Block & Bulk deals", "SIP Calculator",
                  "Advanced Watchlist", "Tutorial Videos", "Fast Customer Support"
                ],
                iconClass: '',
                btnClass: '',
                btnText: 'Get Started',
                badge: null,
                featured: false,
              },
              {
                tier: 'Gold',
                tierClass: 'pro-tier featured',
                price: '7,999',
                period: '/ 6 months',
                features: [
                  "Intraday Scan", "Sector Rotation", "OI Monitor", "Momentum Radar", 
                  "Heat map", "Swing Zone", "Index Mover", "FII - DII Data", 
                  "Traders Diary", "Block & Bulk deals", "SIP Calculator",
                  "Advanced Watchlist", "Tutorial Videos", "VIP Customer Support"
                ],
                iconClass: 'popular-icon',
                btnClass: 'popular-btn',
                btnText: 'Buy Now',
                badge: 'Most Popular',
                featured: true,
              },
              {
                tier: 'Diamond',
                tierClass: 'elite-tier',
                price: '11,999',
                period: '/ 1 year',
                features: [
                  "Intraday Scan", "Sector Rotation", "OI Monitor", "Momentum Radar", 
                  "Heat map", "Swing Zone", "Index Mover", "FII - DII Data", 
                  "Traders Diary", "Block & Bulk deals", "SIP Calculator",
                  "Advanced Watchlist", "Tutorial Videos", "24/7 Dedicated Support",
                  "Advanced Learning Materials"
                ],
                iconClass: 'elite-icon',
                btnClass: '',
                btnText: 'Buy Now',
                badge: null,
                featured: false,
              }
            ].map((plan) => {
              const VISIBLE_COUNT = 8;
              const visibleFeatures = plan.features.slice(0, VISIBLE_COUNT);
              const hiddenFeatures = plan.features.slice(VISIBLE_COUNT);
              const hasHidden = hiddenFeatures.length > 0;
              const isExpanded = expandedPlans[plan.tier];

              return (
                <div key={plan.tier} className={`premium-sub-card ${plan.tierClass}`}>
                  <div className="sub-card-glow"></div>
                  {plan.badge && <div className="popular-badge-new">{plan.badge}</div>}
                  <div className="premium-sub-header">
                    <div className={`tier-badge ${plan.featured ? 'popular-tier' : ''}`}>{plan.tier}</div>
                    <div className="premium-price-box">
                      <span className="currency">₹</span>
                      <span className="price-amount">{plan.price}</span>
                      <span className="period">{plan.period}</span>
                    </div>
                  </div>
                  <div className="sub-features-container">
                    <ul className="premium-feature-list">
                      {visibleFeatures.map((feature, idx) => (
                        <li key={idx} className="premium-feature">
                          <HiCheckCircle className={`feature-check-icon ${plan.iconClass}`} /> {feature}
                        </li>
                      ))}
                    </ul>
                    {hasHidden && (
                      <>
                        <div className={`premium-feature-dropdown ${isExpanded ? 'expanded' : ''}`}>
                          <ul className="premium-feature-list">
                            {hiddenFeatures.map((feature, idx) => (
                              <li key={idx} className="premium-feature">
                                <HiCheckCircle className={`feature-check-icon ${plan.iconClass}`} /> {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <button
                          className={`premium-feature-toggle ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => setExpandedPlans(prev => ({
                            ...prev,
                            [plan.tier]: !prev[plan.tier]
                          }))}
                          type="button"
                        >
                          <span>{isExpanded ? 'Show Less' : `+${hiddenFeatures.length} More Features`}</span>
                          <svg className="toggle-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                  <button className={`premium-sub-btn ${plan.btnClass}`} onClick={openSignUpModal}>{plan.btnText}</button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="testimonial-section">
        <div className="testimonial-container">
          <div className="testimonial-header-premium">
            <h2 className="testimonial-heading">Trusted by 10,000+ Modern Investors</h2>
            <p className="testimonial-subheading">
              Join the institutional-grade trading revolution. Here is why India is switching to Stocks4U.
            </p>
          </div>

          <div className="testimonials-columns-wrapper">
            {[
              {
                items: [
                  { name: "Ananya Sharma", role: "Marketing Lead", text: "I always thought the stock market was only for experts. But Stocks4U made it super simple. The design is clean, and I love the market insights." },
                  { name: "Rohit Mehta", role: "Professional Trader", text: "Stocks4U is perfect for serious traders. The real-time data, advanced charts, and fast execution have made it my primary platform." },
                  { name: "Simran Kaur", role: "Retail Investor", text: "As a beginner, I had no clue where to start. Stocks4U's tutorials and dashboard gave me the confidence to begin with just ₹500!" },
                  { name: "Nikhil Bansal", role: "Software Engineer", text: "The platform's analytics and design impressed me right away. Stocks4U is a must-have for anyone who loves data-driven investing." },
                ],
                duration: 18
              },
              {
                items: [
                  { name: "Priya Reddy", role: "Startup Founder", text: "I run a startup, and Stocks4U helps me invest smartly without wasting time. Alerts, watchlists, and insights - it's all there." },
                  { name: "Karan Joshi", role: "Chartered Accountant", text: "The reporting tools, tax summaries, and clean portfolio view save me a lot of time. It's a well-thought-out app for professionals." },
                  { name: "Megha Rao", role: "Finance Blogger", text: "I've tried many apps, but none have the balance of simplicity and features that Stocks4U offers. It's the one I recommend to all." },
                  { name: "Ishita Deshmukh", role: "UX Designer", text: "The user interface is brilliant. As a designer, I appreciate the detail. As an investor, I value how easy it is to monitor and act quickly." },
                ],
                duration: 22
              },
              {
                items: [
                  { name: "Amit Singh", role: "Investor", text: "I started investing after college and Stocks4U made it so easy. It's user-friendly, secure, and helps you understand every step." },
                  { name: "Aditya Nair", role: "Real Estate Consultant", text: "I was always focused on real estate. But Stocks4U introduced me to stock investing and made diversification effortless." },
                  { name: "Tanvi Patel", role: "Strategy Analyst", text: "Being a student, I wanted to invest small amounts. The fractional investing feature helped me get started with ease." },
                  { name: "Rahul Khanna", role: "Digital Marketer", text: "I'm a numbers guy. Stocks4U gives me the data, trends, and alerts I need without being cluttered or complicated." },
                ],
                duration: 26
              }
            ].map((col, colIndex) => (
              <div key={colIndex} className="testimonial-column">
                <motion.div
                  className="testimonial-column-inner"
                  animate={{ translateY: "-50%" }}
                  transition={{
                    duration: col.duration,
                    repeat: Infinity,
                    ease: "linear",
                    repeatType: "loop",
                  }}
                >
                  {[...new Array(2)].fill(null).map((_, repeatIdx) => (
                    <React.Fragment key={repeatIdx}>
                      {col.items.map((t, idx) => (
                        <div key={idx} className="testimonial-modern-card">
                          <div className="card-quote-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C20.1216 16 21.017 15.1046 21.017 14V9C21.017 7.89543 20.1216 7 19.017 7H16.017C14.9124 7 14.017 7.89543 14.017 9V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M3.01703 21L3.01703 18C3.01703 16.8954 3.91246 16 5.01703 16H8.01703C9.1216 16 10.017 15.1046 10.017 14V9C10.017 7.89543 9.1216 7 8.01703 7H5.01703C3.91246 7 3.01703 7.89543 3.01703 9V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <p className="modern-testimonial-text">{t.text}</p>
                          <div className="modern-testimonial-footer">
                            <div className="modern-avatar-placeholder">
                              {t.name.charAt(0)}
                            </div>
                            <div className="modern-author-info">
                              <span className="modern-author-name">{t.name}</span>
                              <span className="modern-author-role">{t.role}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium About Us Section */}
      <section className="about-us-premium" id="about">
        {/* Decorative Grid Background */}
        <div className="about-neural-grid"></div>
        
        <div className="container">
          <div className="about-header-new">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="about-title-main"
            >
              Pioneering the <span className="gradient-text-neural">Future of Finance</span>
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="about-desc-main"
            >
              Stocks4U is not just a platform; it is an institutional-grade ecosystem designed to empower modern traders with the Neural Intelligence once reserved only for the elite.
            </motion.p>
          </div>

          <div className="about-stats-pulse">
            {[
              { label: "Active Alpha Traders", value: "50,000+" },
              { label: "Cumulative Volume", value: "₹25T+" },
              { label: "Uptime Reliability", value: "99.99%" }
            ].map((stat, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="stat-card-new"
              >
                <div className="stat-glow"></div>
                <h3 className="stat-number-new">{stat.value}</h3>
                <p className="stat-label-new">{stat.label}</p>
              </motion.div>
            ))}
          </div>



          <div className="about-connect-section">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="connect-card-premium"
            >
              <div className="connect-content">
                <h3>Elevate Your Intelligence</h3>
                <p>Join our elite network of traders and receive institutional-grade research in your inbox.</p>
                
                <form onSubmit={handleEmailSubmit} className="connect-form-new">
                  <div className="input-group-premium">
                    <input
                      type="email"
                      placeholder="Your secure email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="premium-input-field"
                    />
                    <button type="submit" className="premium-submit-btn">
                      Join the Elite <HiArrowRight />
                    </button>
                  </div>
                </form>
                
                <div className="connect-meta">
                  <span>124 Traders Joined Today</span>
                  <span>Quantum-Safe Encryption</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" id="contact">
        {/* Stock Ticker */}
        <div className="stock-ticker">
          <div className="ticker-wrapper">
            <div className="ticker-content">
              <div className="ticker-item">
                <span className="ticker-symbol">NIFTY 50</span>
                <span className="ticker-price">19,745.00</span>
                <span className="ticker-change positive">+152.30</span>
              </div>
              <div className="ticker-item">
                <span className="ticker-symbol">SENSEX</span>
                <span className="ticker-price">66,230.00</span>
                <span className="ticker-change positive">+421.80</span>
              </div>
              <div className="ticker-item">
                <span className="ticker-symbol">BANK NIFTY</span>
                <span className="ticker-price">45,120.00</span>
                <span className="ticker-change negative">-89.50</span>
              </div>
              <div className="ticker-item">
                <span className="ticker-symbol">NIFTY IT</span>
                <span className="ticker-price">30,248.75</span>
                <span className="ticker-change positive">+724.30</span>
              </div>
              <div className="ticker-item">
                <span className="ticker-symbol">RELIANCE</span>
                <span className="ticker-price">2,456.75</span>
                <span className="ticker-change positive">+84.50</span>
              </div>
              <div className="ticker-item">
                <span className="ticker-symbol">TCS</span>
                <span className="ticker-price">3,847.50</span>
                <span className="ticker-change positive">+192.25</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="dashboard-container">
          <div className="footer-top-row">
            <div className="footer-brand-section">
              <div className="footer-logo">
                <img src="/images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U Logo" />
              </div>
              <p className="footer-subtitle">Your trusted partner for stock market analysis and trading insights.</p>
              <div className="footer-subscribe-box">
                <input type="email" placeholder="Email address" className="footer-email-input" />
                <button className="footer-subscribe-btn">Submit <span>→</span></button>
              </div>
            </div>
            
            <div className="footer-links-section">
              <div className="footer-link-group">
                <h4>Pages</h4>
                <ul>
                  <li><a href="/">Home</a></li>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/#features">How It Works</a></li>
                  <li><a href="/#pricing">Pricing</a></li>
                  <li><a href="/#contact">Contact Us</a></li>
                </ul>
              </div>
              
              <div className="footer-link-group">
                <h4>Resources</h4>
                <ul>
                  <li><a href="#">Privacy Policy</a></li>
                  <li><a href="#">Terms Of Use</a></li>
                  <li><a href="#">Support</a></li>
                  <li><a href="#">Security</a></li>
                  <li><a href="#">Help Centre</a></li>
                </ul>
              </div>
              
              <div className="footer-link-group contact-group">
                <h4>Contact</h4>
                <p>Email: support@stocks4u.com</p>
                <p>Phone: +91 1234567890</p>
                <div className="footer-social-icons">
                  <a href="#" className="social-icon whatsapp"><FaWhatsapp /></a>
                  <a href="#" className="social-icon telegram"><FaTelegramPlane /></a>
                  <a href="#" className="social-icon instagram"><FaInstagram /></a>
                  <a href="#" className="social-icon facebook"><FaFacebookF /></a>
                  <a href="#" className="social-icon youtube"><FaYoutube /></a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="footer-divider"></div>
          
          <div className="footer-bottom-row">
            <p>stocks4u©2026</p>
            <p>All Rights Reserved</p>
          </div>
        </div>
      </footer>

      <Modal isOpen={isSignInModalOpen} onClose={closeSignInModal}>
        <SignInModal onClose={closeSignInModal} onSuccessfulLogin={onSuccessfulLogin} initialMode={authMode} />
      </Modal>
    </div>
  );
};

export default MainPage;
